import React, { useState, useRef, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Modal,
  ActionIcon,
  Button,
  Card,
  Divider,
  Flex,
  Loader,
  Title,
  TextInput,
  Pagination,
  Table,
  Text,
  Box,
  UnstyledButton,
  SegmentedControl,
  Switch,
  Select,
  LoadingOverlay,
} from "@mantine/core";
import { Link } from "react-router-dom";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getFilteredRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

const MIN_LOADING_TIME = 500;

export default function RideChallengeManagementList({
  effortId,
  refresh,
  isVehicleDataEffort,
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const loadingStartTime = useRef(null);
  const [pageCount, setPageCount] = useState(-1);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [filters, setFilters] = useState({
    scope: "entire",
    hog: "entire",
  });
  const [init, setInit] = useState(false);
  const CancelToken = axios.CancelToken;
  const cancelRef = useRef(null);

  const columns = [
    {
      header: t("common:name"),
      // accessorKey: "recipient_name",
      id: "id",
      enableSorting: false,
      cell: (info) => {
        const { original } = info.row;
        return (
          <UnstyledButton
            component={Link}
            to={`/ride-challenge-interactions/${effortId}/${original.user_id}`}
            target="_blank"
          >
            <Text size="sm" td="underline">
              {original.first_name} {original.last_name}
            </Text>
          </UnstyledButton>
        );
      },
    },
    {
      header: t("common:email"),
      accessorKey: "email",
      id: "email",
      enableSorting: false,
      cell: (info) => info.getValue(),
    },
    {
      header: t("common:phone"),
      accessorKey: "mobile_phone",
      id: "phone",
      enableSorting: false,
      cell: (info) => info.getValue(),
    },
    {
      header: "HOG ID",
      accessorKey: "information.hog_member_number",
      id: "hogId",
      enableSorting: false,
      cell: (info) => info.getValue(),
    },
    // {
    //   header: "Registered",
    //   accessorKey: "information.registered",
    //   id: "registered",
    //   enableSorting: false,
    //   cell: (info) => (info.getValue() === true ? "Yes" : "No"),
    // },
    // {
    //   header: "Entered",
    //   accessorKey: "information.readings_total",
    //   id: "entered",
    //   enableSorting: false,
    //   cell: (info) => (info.getValue() > 0 ? "Yes" : "No"),
    // },
    {
      header: "Total Miles",
      accessorKey: "information.odometer_total",
      id: "odometer",
      enableSorting: false,
      cell: (info) => info.getValue(),
    },
    {
      header: "Readings",
      accessorKey: "information.readings_total",
      id: "readings",
      enableSorting: false,
      cell: (info) => info.getValue(),
    },
  ].filter((f) => (isVehicleDataEffort ? !["entered"].includes(f.id) : true));

  const scopeOptions = [
    { label: t("common:all"), value: "entire" },
    { label: "Readings", value: "has_readings" },
    { label: "No Readings", value: "no_readings" },
  ];

  const hogOptions = [
    { label: t("common:all"), value: "entire" },
    { label: "HOG Members", value: "hog_members" },
    { label: "Non-HOG Members", value: "non_hog_members" },
  ];

  const table = useReactTable({
    data: items,
    columns,
    manualPagination: true,
    enableMultiSort: false,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 20,
      },
    },
  });

  const setLoadingWithMinimum = (isLoading) => {
    if (isLoading) {
      loadingStartTime.current = Date.now();
      setLoading(true);
    } else {
      const elapsedTime = Date.now() - loadingStartTime.current;
      const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsedTime);

      if (remainingTime > 0) {
        setTimeout(() => {
          setLoading(false);
        }, remainingTime);
      } else {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!init) return;
    let p = new Promise((resolve) => {
      resolve(table.setPageIndex(0));
    });
    p.then(() => {
      fetchData();
    });
  }, [JSON.stringify(filters)]);

  useEffect(() => {
    table.setPageIndex(0);
    if (table.getState().pagination.pageIndex === 0) {
      fetchData();
    }
  }, [searchValue]);

  useEffect(() => {
    if (!init) return;
    fetchData();
  }, [JSON.stringify(table.getState().pagination), refresh]);

  function onSearchSubmit() {
    table.setPageIndex(0);
    fetchData();
  }

  function fetchData() {
    setLoadingWithMinimum(true);

    const cancel = cancelRef.current;
    if (cancel) {
      cancel();
    }

    const req = {
      page_size: table.getState().pagination.pageSize,
      page:
        table.getState().pagination.pageIndex === -1
          ? 0
          : table.getState().pagination.pageIndex,
      search_value: searchValue,
      campaign_effort_id: effortId,
      ...filters,
    };

    axios
      .post(`/efforts/${effortId}/retrieve-management-table/`, req, {
        cancelToken: new CancelToken(function executor(c) {
          cancelRef.current = c;
        }),
      })
      .then(({ data }) => {
        setItems(data.response[0].data);
        setTotal(data.response[0].total);
        setPageCount(data.response[0].page_count);
        setInit(true);
        setLoadingWithMinimum(false);
        window.scrollTo(0, 0);
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          // setItems([]);
          // setTotal(0);
          // setPageCount(1);
          setInit(true);
          setLoadingWithMinimum(false);
        }
      });
  }

  return (
    <div>
      <Flex style={{ width: "100%" }} gap="xs" align="center">
        <TextInput
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder={t("common:search")}
          size="md"
          style={{
            flexGrow: 1,
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              onSearchSubmit();
            }
          }}
          rightSection={
            searchValue && (
              <ActionIcon
                variant="light"
                radius="xl"
                color="gray"
                size="xs"
                onClick={() => {
                  setSearchValue("");
                  onSearchSubmit();
                }}
              >
                ×
              </ActionIcon>
            )
          }
        />
      </Flex>
      <Flex gap="xs" align="center">
        <SegmentedControl
          value={filters.scope}
          data={scopeOptions}
          // size="xs"
          onChange={(e) =>
            setFilters({
              ...filters,
              scope: e,
            })
          }
          mt="sm"
        />
        <SegmentedControl
          value={filters.hog}
          data={hogOptions}
          // size="xs"
          onChange={(e) =>
            setFilters({
              ...filters,
              hog: e,
            })
          }
          mt="sm"
        />
      </Flex>
      <Card mt="sm" style={{ position: "relative" }} mih={250}>
        <LoadingOverlay visible={loading} />
        <Table.ScrollContainer minWidth={1000}>
          <Table striped>
            <Table.Thead>
              <Table.Tr>
                {table.getFlatHeaders().map((header) => (
                  <Table.Th key={header.id}>
                    {header.column.columnDef.header}
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {!loading && table.getRowModel().rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={table.getFlatHeaders().length}>
                    No results
                  </Table.Td>
                </Table.Tr>
              )}
              {table.getRowModel().rows.map((row, index) => {
                return (
                  <Table.Tr key={index}>
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <Table.Td key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </Table.Td>
                      );
                    })}
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>
      <Flex mt="lg" gap="xs" align="center">
        <Pagination
          value={table.getState().pagination.pageIndex + 1}
          onChange={(e) => {
            table.setPageIndex(e - 1);
          }}
          total={pageCount}
        />
        <Select
          size="sm"
          style={{ width: "80px" }}
          value={`${table.getState().pagination.pageSize}`}
          onChange={(e) => table.setPageSize(parseInt(e))}
          data={[5, 10, 20, 30, 40, 50].map((pageSize) => ({
            label: `${pageSize}`,
            value: `${pageSize}`,
          }))}
        />
      </Flex>
      <Text mb="lg" size="sm" mt="sm">
        {total} {t("common:total")}{" "}
        {total === 1 ? t("common:item_singular") : t("common:item_plural")}
      </Text>
    </div>
  );
}
